import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaHeadphones, FaClipboardList, FaUsers, FaChartLine, FaTrophy } from 'react-icons/fa';
import { GiMeditation } from 'react-icons/gi';

import { useAppSelector } from '../store/hooks';

const WelcomeScreen: React.FC = () => {
  const navigate = useNavigate();
  const { user: currentUser } = useAppSelector((state) => state.user);
  const [isVisible, setIsVisible] = useState(false);
  const [circleVisible, setCircleVisible] = useState(false);
  const [currentIconIndex, setCurrentIconIndex] = useState(0);
  const [isComplete, setIsComplete] = useState(false);
  const [isExiting, setIsExiting] = useState(false);

  const icons = [
    { component: FaHeadphones, color: "text-orange-500" },
    { component: GiMeditation, color: "text-amber-500" },
    { component: FaClipboardList, color: "text-rose-500" },
    { component: FaUsers, color: "text-orange-600" },
    { component: FaChartLine, color: "text-amber-600" },
    { component: FaTrophy, color: "text-rose-600" }
  ];

  useEffect(() => {
    // Check if already seen
    if (currentUser?.id && localStorage.getItem(`welcome_seen_${currentUser.id}`) === 'true') {
      navigate('/dashboard');
      return;
    }

    // Trigger animations on mount
    setTimeout(() => setIsVisible(true), 100);
    setTimeout(() => setCircleVisible(true), 300);

    // Timer for progress completion (8s matches CSS animation)
    const completionTimer = setTimeout(() => {
      setIsComplete(true);
    }, 8000);

    // Start exit animation
    const exitTimer = setTimeout(() => {
      setIsExiting(true);
    }, 8800);

    // Navigate to dashboard
    const navTimer = setTimeout(() => {
      if (currentUser?.id) {
        localStorage.setItem(`welcome_seen_${currentUser.id}`, 'true');
      }
      navigate('/dashboard');
    }, 9800);

    // Cycle through icons
    const iconInterval = setInterval(() => {
      setCurrentIconIndex((prev) => (prev + 1) % icons.length);
    }, 1500);

    return () => {
      clearTimeout(completionTimer);
      clearTimeout(exitTimer);
      clearTimeout(navTimer);
      clearInterval(iconInterval);
    };
  }, [navigate, currentUser]);

  return (
    <>
      {/* Fixed Background Layer to match Dashboard */}
      <div className="fixed inset-0 bg-[#FFF8F0] z-0" />

      <div
        className={`min-h-screen bg-[#FFF8F0] flex items-center justify-center p-4 sm:p-6 lg:p-8 relative overflow-hidden transition-all duration-1000 ease-in-out z-10 ${isExiting ? '-translate-y-full opacity-0' : 'translate-y-0 opacity-100'
          }`}
      >
        <div className="absolute inset-0 overflow-hidden pointer-events-none z-0">
          <div className="absolute top-0 -left-1/4 w-96 h-96 bg-gradient-to-br from-orange-300/50 to-orange-200/40 rounded-full blur-3xl animate-blob-slow"></div>
          <div className="absolute top-1/4 -right-1/4 w-96 h-96 bg-gradient-to-br from-orange-400/50 to-orange-300/40 rounded-full blur-3xl animate-blob-slow" style={{ animationDelay: "5s" }}></div>
          <div className="absolute bottom-0 left-1/4 w-80 h-80 bg-gradient-to-br from-orange-200/40 to-orange-100/30 rounded-full blur-3xl animate-blob-slow" style={{ animationDelay: "10s" }}></div>
          <div className="absolute top-1/2 right-1/3 w-72 h-72 bg-gradient-to-br from-orange-300/40 to-orange-200/30 rounded-full blur-3xl animate-blob-slow" style={{ animationDelay: "15s" }}></div>

          {/* Floating Shapes */}
          <div className="absolute top-[15%] right-[20%] w-16 h-16 border-4 border-orange-300/20 rounded-full animate-float opacity-50"></div>
          <div className="absolute bottom-[20%] left-[15%] w-12 h-12 border-4 border-amber-300/20 rounded-full animate-float opacity-50" style={{ animationDelay: "2s" }}></div>

          {/* Additional Elements */}
          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] border border-orange-100/30 rounded-full animate-pulse-slow pointer-events-none"></div>
          <div className="absolute top-1/3 left-1/4 w-2 h-2 bg-orange-400/40 rounded-full animate-float" style={{ animationDelay: "1.5s" }}></div>
          <div className="absolute top-2/3 right-1/3 w-3 h-3 bg-amber-400/40 rounded-full animate-float" style={{ animationDelay: "3.5s" }}></div>
          <div className="absolute bottom-1/4 right-1/4 w-2 h-2 bg-rose-400/40 rounded-full animate-float" style={{ animationDelay: "0.5s" }}></div>
        </div>

        {/* Content Container */}
        <div className="relative z-10 max-w-4xl w-full flex flex-col-reverse md:flex-row items-center justify-between gap-12 sm:gap-12 lg:gap-16">
          {/* Text Content - Left Side (Bottom on mobile) */}
          <div
            className={`flex-1 transition-all duration-1000 text-center md:text-left ${isVisible ? 'opacity-100 translate-y-0 md:translate-x-0' : 'opacity-0 translate-y-8 md:translate-y-0 md:-translate-x-8'
              }`}
          >
            <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-serif font-normal text-gray-900 leading-tight mb-4">
              <span className="block">Welcome to</span>
              <span className="block">your new</span>
              <span className="block">private oasis</span>
            </h1>
          </div>

          {/* White Circle - Right Side (Top on mobile) */}
          <div
            className={`flex-shrink-0 transition-all duration-1000 relative ${circleVisible ? 'opacity-100 scale-100' : 'opacity-0 scale-90'
              }`}
          >
            {/* Floating Wrapper */}
            <div
              className="w-64 h-64 sm:w-80 sm:h-80 md:w-96 md:h-96 lg:w-[500px] lg:h-[500px] relative"
              style={{
                animation: 'float 6s ease-in-out infinite',
              }}
            >
              {/* Progress Loader */}
              <svg
                className={`absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-[106%] h-[106%] rotate-[-90deg] z-0 transition-all duration-1000 ${isComplete ? 'drop-shadow-[0_0_15px_rgba(251,146,60,0.8)]' : ''
                  }`}
                viewBox="0 0 100 100"
              >

                {/* Progress */}
                <circle
                  cx="50"
                  cy="50"
                  r="48"
                  fill="none"
                  stroke="#fb923c"
                  strokeWidth="1.5"
                  strokeDasharray="302"
                  strokeDashoffset="302"
                  strokeLinecap="round"
                  className="animate-progress"
                />
              </svg>

              {/* White Circle Content */}
              <div className="absolute inset-0 bg-white rounded-full flex items-center justify-center overflow-hidden z-10">
                {icons.map((IconData, index) => (
                  <div
                    key={index}
                    className={`absolute inset-0 flex items-center justify-center transition-all duration-300 ${index === currentIconIndex ? 'opacity-100' : 'opacity-0'
                      }`}
                  >
                    <IconData.component
                      className={`w-32 h-32 sm:w-40 sm:h-40 md:w-48 md:h-48 lg:w-64 lg:h-64 ${IconData.color}`}
                    />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* CSS Animations */}
        <style>{`
        @keyframes float {
          0%, 100% {
            transform: translateY(0px);
          }
          50% {
            transform: translateY(-20px);
          }
        }
      `}</style>
      </div>
    </>
  );
};

export default WelcomeScreen;
